import { Box, Tag as ChakraTag, TagCloseButton, TagLabel, ThemeTypings } from '@chakra-ui/react';
import { Tag } from '@main/complykit/components/tag';

import { Combobox, ComboboxProps, withMultiCombobox } from '../combobox';
import { labelColorFromColorScheme } from '../legacy-color-scheme';
import { OverflowContainer } from '../overflow-tooltip';

export type TagOption = {
  value: string;
  label?: string | null;
  colorScheme?: ThemeTypings['colorSchemes'];
  bgColor?: string;
  textColor?: string;
  order?: string | null;
};

type TagOptionProps = TagOption & {
  removeProps?: JSX.IntrinsicElements['div'];
};

function TagOptionContent({
  value,
  label,
  colorScheme,
  bgColor,
  textColor,
  removeProps,
}: TagOptionProps) {
  const displayLabel = label || value;

  /* DEFERRED: org-configured options (risk levels, tiers) store raw hex
   * bg/text colors in data; the closed LabelColor set has no home for those
   * until that data is migrated, so they stay on the Chakra Tag. */
  if (bgColor || textColor) {
    return (
      <ChakraTag
        minW={0}
        maxW="100%"
        overflow="hidden"
        colorScheme={colorScheme}
        bgColor={bgColor}
        color={textColor}
      >
        <OverflowContainer.Tooltip
          label={displayLabel}
          placement="top"
          fontSize="sm"
          openDelay={500}
        >
          <TagLabel flex={1} minW={0} isTruncated>
            {displayLabel}
          </TagLabel>
        </OverflowContainer.Tooltip>
        {removeProps && (
          <Box
            as={TagCloseButton}
            flexShrink={0}
            {...removeProps}
            aria-label={`Remove ${displayLabel}`}
          />
        )}
      </ChakraTag>
    );
  }

  const color = labelColorFromColorScheme(colorScheme);
  const tagLabel = (
    <OverflowContainer.Tooltip label={displayLabel} placement="top" fontSize="sm" openDelay={500}>
      {/* Its own truncating block, because overflow is detected on this
       * element: the tag's outer anatomy shrinks its label slot, so only
       * an inner block can report scrollWidth past its clip. */}
      <Box
        as="span"
        display="inline-block"
        maxW="100%"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        verticalAlign="bottom"
      >
        {displayLabel}
      </Box>
    </OverflowContainer.Tooltip>
  );

  if (!removeProps) {
    return <Tag color={color}>{tagLabel}</Tag>;
  }

  /* react-select's removeProps adapt onto Tag's native remove so the control
   * renders INSIDE the pill: onClick is removeValue and ignores its event
   * (so it becomes the plain onRemove callback), onTouchEnd is covered by
   * the RAC Button's press handling, and onMouseDown's preventDefault (keep
   * the combobox input focused) by the button's preventFocusOnPress. */
  return (
    <Tag
      color={color}
      onRemove={() => (removeProps.onClick as (() => void) | undefined)?.()}
      removeLabel={`Remove ${displayLabel}`}
    >
      {tagLabel}
    </Tag>
  );
}

export const defaultProps = {
  getOptionValue: (option) => option.value,
  getOptionLabel: (option) => option.label || option.value,

  components: {
    Option: TagOptionContent,
    SelectedOption: TagOptionContent,
  },
} satisfies ComboboxProps<TagOption>;

export function EditableTag<TOption extends TagOption, IsMulti extends boolean = false>(
  props: ComboboxProps<TOption, IsMulti>,
) {
  const multiComboboxProps = withMultiCombobox(props);

  return (
    <Combobox
      {...defaultProps}
      {...multiComboboxProps}
      components={{
        ...defaultProps.components,
        ...props.components,
      }}
    />
  );
}

EditableTag.Option = defaultProps.components.Option;
